<script lang="ts">
	import type { CollectionItem } from '$lib/api/velatlas/response.types';
	import { CITY_FEATURE_TYPE_SINGLE_LABELS } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';

	const DEFAULT_FETCH_PRIORITY = 'auto';

	const {
		item,
		loading = 'lazy',
		fetchPriority = DEFAULT_FETCH_PRIORITY,
		withCountryName = true
	}: {
		item: CollectionItem;
		loading?: 'eager' | 'lazy';
		fetchPriority?: 'high' | 'auto';
		withCountryName?: boolean;
	} = $props();

	const nav = $derived<NavLink>(
		UrlUtil.toCityFeature({
			countrySlug: item.country.slug,
			citySlug: item.city.slug,
			type: item.type,
			featureSlug: item.slug
		})
	);
	const ariaLabelledBy = $derived<string>(`item-${item.city.slug}-${item.slug}`);
</script>

<div class="group surface card card-border">
	<a
		class="contents"
		href="/{nav.href}"
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
		aria-labelledby={ariaLabelledBy}
	>
		<figure class="thumb-wrap">
			<img
				class="thumb"
				src={UrlUtil.getFullImageUrl(item.image.filename)}
				alt={item.image.altText}
				width={item.image.width}
				height={item.image.height}
				{loading}
				fetchpriority={fetchPriority === DEFAULT_FETCH_PRIORITY ? undefined : fetchPriority}
			/>
		</figure>
		<div class="card-body flex grow flex-col p-5">
			<span class="eyebrow">
				{#if withCountryName}
					{item.country.label.toUpperCase()},
				{/if}
				{item.city.label.toUpperCase()}
			</span>
			<h2 id={ariaLabelledBy} class="card-title text-xl leading-snug font-medium">
				{item.englishName}
			</h2>
			<div class="mt-auto flex items-center justify-between border-t border-base-content/5 pt-4">
				<span class="eyebrow">{CITY_FEATURE_TYPE_SINGLE_LABELS[item.type].toLowerCase()}</span>
				<span class="cta"> read more → </span>
			</div>
		</div>
	</a>
</div>

<style lang="postcss">
	@reference "$style";

	.surface {
		@apply h-full overflow-hidden border-2 border-transparent bg-base-200 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-base-300 hover:bg-base-300 hover:shadow-md;
	}
	.thumb-wrap {
		@apply relative aspect-4/3 overflow-hidden bg-neutral;
	}
	.thumb {
		@apply h-full w-full object-cover contrast-105 grayscale-35 transition-all duration-500 ease-out group-hover:scale-105 group-hover:grayscale-0;
	}
	.eyebrow {
		@apply font-mono text-xs font-light tracking-widest text-base-content/70;
	}
	.cta {
		@apply mt-auto inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-primary;
	}
</style>
